import { useImperativeHandle } from 'react';
import { useUpdateEffect } from 'react-use';

import { Form, FormContent, Scroll } from '@dreryk/react-components';

import { TemplatesFormProps } from '../../../../../shared/types';
import { AdditionalDetailsTextInput } from '../../../shared/formFields/magistralDrug/AdditionalDetailsTextInput';
import { DrugFormTextInput } from '../../../shared/formFields/magistralDrug/DrugFormTextInput';
import { MedicineRawMaterialTable } from '../../../shared/formFields/magistralDrug/medicineRawMaterials/MedicineRawMaterialTable';
import { MagistralDrugTemplateListItem } from '../MagistralDrugTemplates.types';
import { mapMagistralDrugTemplateListItemToTemplateFormBean } from '../MagistralDrugTemplates.utils';

import { useMagistralDrugTemplateFormController } from './MagistralDrugTemplateForm.hooks';
import { MagistralDrugTemplateFormController } from './MagistralDrugTemplateForm.types';
import { MagistralDrugTemplateTemplateNameTextInput } from './MagistralDrugTemplateTemplateNameTextInput';

export type MagistralDrugTemplateFormChildrenRenderProps = {
   handleSubmit: MagistralDrugTemplateFormController['handleSubmit'];
   formState: MagistralDrugTemplateFormController['formState'];
};

type MagistralDrugTemplateFormProps = TemplatesFormProps<
   MagistralDrugTemplateListItem,
   MagistralDrugTemplateFormChildrenRenderProps
>;

export const MagistralDrugTemplateForm = (props: MagistralDrugTemplateFormProps): JSX.Element => {
   const { register, reset, formState, handleSubmit, watch, control } = useMagistralDrugTemplateFormController(
      props.template ? mapMagistralDrugTemplateListItemToTemplateFormBean(props.template) : undefined
   );

   const {
      fieldsRef: { current: formFieldsRef },
   } = control;

   useImperativeHandle(
      props.templatesApiRef,
      () => ({
         onActiveItemClick: () => {
            formFieldsRef.drugName?.ref?.focus?.();
         },
      }),
      [formFieldsRef.drugName?.ref]
   );

   useUpdateEffect(() => {
      if (formFieldsRef.drugName) {
         formFieldsRef.drugName.ref.focus?.();
      }
   }, [formFieldsRef.drugName?.ref]);

   useUpdateEffect(() => {
      if (props.template) {
         reset(mapMagistralDrugTemplateListItemToTemplateFormBean(props.template));
      }
   }, [props.template]);

   useUpdateEffect(() => {
      if (formState.isDirty) {
         props.onDirty();
         return;
      }
      props.onPristine();
   }, [formState.isDirty]);

   const currentDrugName = watch('drugName');

   useUpdateEffect(() => {
      props.onTemplateNameChange(currentDrugName ?? '');
   }, [currentDrugName]);

   return (
      <Scroll testId={'magistralDrug'}>
         <Form noValidate>
            <FormContent testId={'content'}>
               <MagistralDrugTemplateTemplateNameTextInput register={register} template={props.template} />
               <MedicineRawMaterialTable register={register} />
               <DrugFormTextInput register={register} />
               <AdditionalDetailsTextInput register={register} />
            </FormContent>
            {props.children({ handleSubmit: handleSubmit, formState: formState })}
         </Form>
      </Scroll>
   );
};
